import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createShop, addService, addHomeService, toggleHomeService } from "../utils/api";

const CLOUD_NAME = "dvxt5fnic";
const UPLOAD_PRESET = "oyxievyx";

export default function CreateShop() {
  const navigate = useNavigate();
  const [shopForm, setShopForm] = useState({ name: "", description: "", address: "", city: "", phone: "", opening_time: "09:00", closing_time: "21:00" });
  const [services, setServices] = useState([{ name: "", description: "", duration: 30, price: "" }]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createdShop, setCreatedShop] = useState(null);
  const [showNoShop, setShowNoShop] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // ── Home services step ──
  const [homeServices, setHomeServices] = useState([{ name: "", description: "", duration: 30, price: "", advance_amount: "" }]);

  const handleShopChange = (field, value) => setShopForm((prev) => ({ ...prev, [field]: value }));
  const handleServiceChange = (index, field, value) => setServices((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  const addServiceRow = () => setServices((prev) => [...prev, { name: "", description: "", duration: 30, price: "" }]);
  const removeServiceRow = (index) => { if (services.length > 1) setServices((prev) => prev.filter((_, i) => i !== index)); };

  const handleHomeServiceChange = (index, field, value) => setHomeServices((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  const addHomeServiceRow = () => setHomeServices((prev) => [...prev, { name: "", description: "", duration: 30, price: "", advance_amount: "" }]);
  const removeHomeServiceRow = (index) => { if (homeServices.length > 1) setHomeServices((prev) => prev.filter((_, i) => i !== index)); };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Photo must be less than 5MB."); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setError("");
  };

  const uploadPhoto = async (shopId) => {
    if (!photoFile) return;
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", photoFile);
      formData.append("upload_preset", UPLOAD_PRESET);
      formData.append("folder", "trimspot/shops");
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.secure_url) {
        const token = localStorage.getItem("token");
        await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/upload/shop-photo`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ photoUrl: data.secure_url, shopId }),
        });
      }
    } catch (err) {
      console.error("Photo upload failed:", err);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleCreateShop = async () => {
    if (!shopForm.name || !shopForm.address || !shopForm.city) { setError("Shop name, address and city are required."); return; }
    setLoading(true); setError("");
    try {
      const res = await createShop(shopForm);
      const newShop = res.data.shop;
      setCreatedShop(newShop);
      if (photoFile) await uploadPhoto(newShop.id);
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.message || "Could not create shop.";
      if (msg.includes("already have a shop")) { navigate("/dashboard"); }
      else { setError(msg); }
    } finally { setLoading(false); }
  };

  const handleAddServices = async () => {
    const validServices = services.filter((s) => s.name && s.price);
    if (validServices.length === 0) { setError("Add at least one service with name and price."); return; }
    setLoading(true); setError("");
    try {
      for (const service of validServices) await addService(createdShop.id, service);
      setStep(3); // go to home services step
    } catch (err) { setError(err.response?.data?.message || "Could not add services."); }
    finally { setLoading(false); }
  };

  const handleAddHomeServices = async () => {
    const validHomeServices = homeServices.filter((s) => s.name && s.price && s.advance_amount);
    if (validHomeServices.length === 0) { setError("Add at least one home service with name, price and advance amount."); return; }
    setLoading(true); setError("");
    try {
      await toggleHomeService(createdShop.id, true);
      for (const service of validHomeServices) {
        await addHomeService({ ...service, shop_id: createdShop.id });
      }
      setStep(4);
    } catch (err) { setError(err.response?.data?.message || "Could not add home services."); }
    finally { setLoading(false); }
  };

  const handleSkipHomeServices = () => setStep(4);

  if (showNoShop) {
    return (
      <div style={styles.center}>
        <div style={styles.noShopCard}>
          <p style={{ fontSize: "60px", marginBottom: "16px" }}>🏪</p>
          <h2 style={styles.noShopTitle}>No Shop Yet!</h2>
          <p style={styles.noShopText}>Create your shop profile to start receiving bookings.</p>
          <button onClick={() => setShowNoShop(false)} style={styles.createShopBtn}>+ Create My Shop</button>
        </div>
      </div>
    );
  }

  // ── Step 4: Success ──
  if (step === 4) {
    return (
      <div style={styles.center}>
        <div style={styles.successCard}>
          <p style={{ fontSize: "60px", marginBottom: "12px" }}>🎉</p>
          <h2 style={styles.successTitle}>Shop Created!</h2>
          <p style={styles.successText}><strong style={{ color: "white" }}>{createdShop?.name}</strong> is now live!</p>
          <div style={styles.nextSteps}>
            <p style={styles.nextStepsTitle}>📋 Next Steps</p>
            <p style={styles.nextStep}>✅ Shop profile created</p>
            <p style={styles.nextStep}>✅ Services added</p>
            <p style={styles.nextStepPending}>⏳ Subscribe to receive bookings</p>
          </div>
          <div style={styles.successBtns}>
            <button onClick={() => navigate("/dashboard")} style={styles.dashBtn}>Go to Dashboard →</button>
            <button onClick={() => navigate(`/shops/${createdShop?.id}`)} style={styles.viewBtn}>View My Shop</button>
          </div>
        </div>
      </div>
    );
  }

  const stepLabels = [
    { num: 1, label: "Shop Info" },
    { num: 2, label: "Services" },
    { num: 3, label: "Home (Optional)" },
    { num: 4, label: "Done!" },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button onClick={() => step === 1 ? setShowNoShop(true) : setStep(step - 1)} style={styles.backBtn}>← Back</button>
        <h2 style={styles.headerTitle}>
          {step === 1 ? "🏪 Create Shop" : step === 2 ? "✂️ Add Services" : step === 3 ? "🏠 Home Services" : "Done!"}
        </h2>
        <div />
      </div>

      {/* Step indicators */}
      <div style={styles.stepRow}>
        {stepLabels.map((s) => (
          <div key={s.num} style={styles.stepItem}>
            <div style={{ ...styles.stepCircle, ...(step === s.num ? styles.stepActive : {}), ...(step > s.num ? styles.stepDone : {}) }}>
              {step > s.num ? "✓" : s.num}
            </div>
            <span style={styles.stepLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      <div style={styles.container}>
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* ── STEP 1: Shop Info ── */}
        {step === 1 && (
          <div style={styles.formCard}>
            <h3 style={styles.sectionTitle}>Shop Details</h3>

            <label style={styles.label}>Shop Photo (Optional)</label>
            <div style={styles.photoUploadBox}>
              {photoPreview ? (
                <div style={styles.photoPreviewWrap}>
                  <img src={photoPreview} alt="Shop" style={styles.photoPreview} />
                  <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} style={styles.removePhotoBtn}>✕ Remove</button>
                </div>
              ) : (
                <label htmlFor="shopPhoto" style={styles.photoUploadLabel}>
                  <span style={{ fontSize: "36px" }}>📷</span>
                  <span style={styles.photoUploadText}>Click to upload shop photo</span>
                  <span style={styles.photoUploadHint}>JPG, PNG • Max 5MB</span>
                </label>
              )}
              <input id="shopPhoto" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} />
            </div>

            <label style={styles.label}>Shop Name *</label>
            <input placeholder="e.g. Raju Hair Salon" value={shopForm.name} onChange={(e) => handleShopChange("name", e.target.value)} style={styles.input} />

            <label style={styles.label}>Description</label>
            <textarea placeholder="Tell customers about your shop..." value={shopForm.description} onChange={(e) => handleShopChange("description", e.target.value)} style={styles.textarea} rows={3} />

            <label style={styles.label}>Full Address *</label>
            <input placeholder="e.g. Main Road, Station Bazaar" value={shopForm.address} onChange={(e) => handleShopChange("address", e.target.value)} style={styles.input} />

            <label style={styles.label}>City *</label>
            <input placeholder="e.g. Brahmapur" value={shopForm.city} onChange={(e) => handleShopChange("city", e.target.value)} style={styles.input} />

            <label style={styles.label}>Phone Number</label>
            <input placeholder="e.g. 9876543210" value={shopForm.phone} onChange={(e) => handleShopChange("phone", e.target.value)} style={styles.input} />

            <label style={styles.label}>Working Hours</label>
            <div style={styles.timeRow}>
              <div style={styles.timeGroup}>
                <span style={styles.timeLabel}>Opens at</span>
                <input type="time" value={shopForm.opening_time} onChange={(e) => handleShopChange("opening_time", e.target.value)} style={styles.timeInput} />
              </div>
              <span style={styles.timeDash}>→</span>
              <div style={styles.timeGroup}>
                <span style={styles.timeLabel}>Closes at</span>
                <input type="time" value={shopForm.closing_time} onChange={(e) => handleShopChange("closing_time", e.target.value)} style={styles.timeInput} />
              </div>
            </div>

            <button onClick={handleCreateShop} style={styles.nextBtn} disabled={loading || photoUploading}>
              {loading || photoUploading ? "Creating..." : "Next → Add Services"}
            </button>
          </div>
        )}

        {/* ── STEP 2: Salon Services ── */}
        {step === 2 && (
          <div style={styles.formCard}>
            <div style={styles.shopCreatedBanner}>✅ <strong>{createdShop?.name}</strong> created! Now add your services.</div>
            <h3 style={styles.sectionTitle}>Your Services</h3>
            <p style={styles.sectionHint}>Add services you offer (haircut, shave, facial etc.)</p>
            {services.map((service, index) => (
              <div key={index} style={styles.serviceRow}>
                <div style={styles.serviceRowHeader}>
                  <span style={styles.serviceNum}>Service {index + 1}</span>
                  {services.length > 1 && <button onClick={() => removeServiceRow(index)} style={styles.removeBtn}>✕ Remove</button>}
                </div>
                <input placeholder="Service name e.g. Haircut *" value={service.name} onChange={(e) => handleServiceChange(index, "name", e.target.value)} style={styles.input} />
                <input placeholder="Short description (optional)" value={service.description} onChange={(e) => handleServiceChange(index, "description", e.target.value)} style={styles.input} />
                <div style={styles.durationPriceRow}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Duration (mins) *</label>
                    <select value={service.duration} onChange={(e) => handleServiceChange(index, "duration", Number(e.target.value))} style={styles.input}>
                      {[15, 20, 30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d} mins</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Price (₹) *</label>
                    <input type="number" placeholder="e.g. 100" value={service.price} onChange={(e) => handleServiceChange(index, "price", e.target.value)} style={styles.input} min="0" />
                  </div>
                </div>
              </div>
            ))}
            <button onClick={addServiceRow} style={styles.addServiceBtn}>+ Add Another Service</button>
            <div style={styles.navRow}>
              <button onClick={() => setStep(1)} style={styles.prevBtn}>← Back</button>
              <button onClick={handleAddServices} style={styles.nextBtn} disabled={loading}>{loading ? "Saving..." : "Next → Home Services"}</button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Home Services (Optional) ── */}
        {step === 3 && (
          <div style={styles.formCard}>
            <div style={styles.homeStepBanner}>
              <span style={{ fontSize: "28px" }}>🏠</span>
              <div>
                <p style={styles.homeStepTitle}>Offer Home Services? <span style={styles.optionalTag}>Optional</span></p>
                <p style={styles.homeStepSubtext}>Let customers book your services at their doorstep. You can also add or edit this later from <strong>Edit Shop → 🏠 Home tab</strong>.</p>
              </div>
            </div>

            <h3 style={styles.sectionTitle}>Home Services</h3>
            <p style={styles.sectionHint}>Add services available at customer's home (name, price, advance amount required)</p>

            {homeServices.map((service, index) => (
              <div key={index} style={styles.homeServiceRow}>
                <div style={styles.serviceRowHeader}>
                  <span style={{ ...styles.serviceNum, color: "#4caf50" }}>Home Service {index + 1}</span>
                  {homeServices.length > 1 && <button onClick={() => removeHomeServiceRow(index)} style={styles.removeBtn}>✕ Remove</button>}
                </div>
                <input placeholder="Service name e.g. Home Haircut *" value={service.name} onChange={(e) => handleHomeServiceChange(index, "name", e.target.value)} style={styles.input} />
                <input placeholder="Short description (optional)" value={service.description} onChange={(e) => handleHomeServiceChange(index, "description", e.target.value)} style={styles.input} />
                <div style={styles.durationPriceRow}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Duration (mins) *</label>
                    <select value={service.duration} onChange={(e) => handleHomeServiceChange(index, "duration", Number(e.target.value))} style={styles.input}>
                      {[15, 20, 30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d} mins</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Price (₹) *</label>
                    <input type="number" placeholder="e.g. 300" value={service.price} onChange={(e) => handleHomeServiceChange(index, "price", e.target.value)} style={styles.input} min="0" />
                  </div>
                </div>
                <label style={styles.label}>Advance Amount (₹) *</label>
                <input type="number" placeholder="e.g. 100 (paid online to confirm)" value={service.advance_amount} onChange={(e) => handleHomeServiceChange(index, "advance_amount", e.target.value)} style={styles.input} min="0" />
              </div>
            ))}

            <button onClick={addHomeServiceRow} style={{ ...styles.addServiceBtn, borderColor: "rgba(76,175,80,0.4)", color: "#4caf50" }}>+ Add Another Home Service</button>

            {/* Skip note */}
            <div style={styles.skipNote}>
              💡 You can always add home services later from <strong>Edit Shop → 🏠 Home tab</strong>
            </div>

            <div style={styles.navRow}>
              <button onClick={handleSkipHomeServices} style={styles.skipBtn}>Skip for Now</button>
              <button onClick={handleAddHomeServices} style={{ ...styles.nextBtn, background: "#4caf50" }} disabled={loading}>
                {loading ? "Saving..." : "✅ Save & Finish"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", paddingBottom: "40px" },
  center: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: "20px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" },
  headerTitle: { fontSize: "17px", fontWeight: "bold", color: "white" },
  backBtn: { background: "none", border: "1px solid var(--border)", color: "var(--text-secondary)", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontSize: "14px" },
  stepRow: { display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", padding: "16px 20px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)", flexWrap: "wrap" },
  stepItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" },
  stepCircle: { width: "36px", height: "36px", borderRadius: "50%", background: "var(--bg-card)", color: "var(--text-muted)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "14px" },
  stepActive: { background: "var(--accent)", color: "white", border: "1px solid var(--accent)" },
  stepDone: { background: "#4caf50", color: "white", border: "1px solid #4caf50" },
  stepLabel: { fontSize: "10px", color: "var(--text-muted)", textAlign: "center", maxWidth: "70px" },
  container: { maxWidth: "560px", margin: "0 auto", padding: "20px 16px" },
  errorBox: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(233,69,96,0.3)" },
  formCard: { background: "var(--bg-card)", borderRadius: "16px", padding: "24px 20px", border: "1px solid var(--border)" },
  sectionTitle: { fontSize: "16px", fontWeight: "bold", color: "white", marginBottom: "6px" },
  sectionHint: { color: "var(--text-muted)", fontSize: "13px", marginBottom: "20px" },
  label: { display: "block", color: "var(--text-secondary)", fontSize: "13px", marginBottom: "6px", fontWeight: "500" },
  input: { width: "100%", padding: "11px 13px", borderRadius: "8px", fontSize: "14px", marginBottom: "16px", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "11px 13px", borderRadius: "8px", fontSize: "14px", marginBottom: "16px", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" },
  photoUploadBox: { marginBottom: "20px" },
  photoUploadLabel: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px", border: "2px dashed var(--border)", borderRadius: "12px", padding: "28px 20px", cursor: "pointer", background: "var(--bg-secondary)" },
  photoUploadText: { color: "var(--text-secondary)", fontSize: "14px", fontWeight: "600" },
  photoUploadHint: { color: "var(--text-muted)", fontSize: "12px" },
  photoPreviewWrap: { position: "relative", borderRadius: "12px", overflow: "hidden", border: "1px solid var(--border)" },
  photoPreview: { width: "100%", height: "200px", objectFit: "cover", display: "block" },
  removePhotoBtn: { position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.7)", color: "white", border: "none", borderRadius: "6px", padding: "6px 12px", cursor: "pointer", fontSize: "13px" },
  timeRow: { display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" },
  timeGroup: { flex: 1, minWidth: "120px" },
  timeLabel: { display: "block", color: "var(--text-muted)", fontSize: "12px", marginBottom: "6px" },
  timeInput: { width: "100%", padding: "10px", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  timeDash: { color: "var(--text-muted)", fontSize: "20px", marginTop: "16px" },
  shopCreatedBanner: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "12px 16px", borderRadius: "8px", marginBottom: "20px", fontSize: "14px", border: "1px solid rgba(76,175,80,0.3)" },
  serviceRow: { border: "1px solid var(--border)", borderRadius: "10px", padding: "16px", marginBottom: "16px", background: "var(--bg-secondary)" },
  homeServiceRow: { border: "1px solid rgba(76,175,80,0.25)", borderRadius: "10px", padding: "16px", marginBottom: "16px", background: "rgba(76,175,80,0.04)" },
  serviceRowHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" },
  serviceNum: { fontWeight: "bold", color: "var(--accent)", fontSize: "13px" },
  removeBtn: { background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "13px" },
  durationPriceRow: { display: "flex", gap: "12px" },
  addServiceBtn: { width: "100%", padding: "12px", background: "var(--bg-secondary)", border: "1px dashed var(--border)", borderRadius: "8px", color: "var(--text-muted)", cursor: "pointer", fontSize: "14px", marginBottom: "16px" },
  navRow: { display: "flex", gap: "12px" },
  prevBtn: { flex: 1, padding: "13px", background: "none", border: "1px solid var(--border)", color: "var(--text-secondary)", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  nextBtn: { flex: 2, padding: "13px", background: "var(--accent)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px", fontWeight: "bold" },
  skipBtn: { flex: 1, padding: "13px", background: "none", border: "1px solid var(--border)", color: "var(--text-muted)", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },

  // Home step banner
  homeStepBanner: { display: "flex", gap: "14px", alignItems: "flex-start", background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.25)", borderRadius: "12px", padding: "16px", marginBottom: "20px" },
  homeStepTitle: { fontWeight: "bold", color: "white", fontSize: "15px", margin: "0 0 6px", display: "flex", alignItems: "center", gap: "8px" },
  homeStepSubtext: { color: "var(--text-muted)", fontSize: "13px", margin: 0, lineHeight: 1.6 },
  optionalTag: { background: "rgba(76,175,80,0.2)", color: "#4caf50", fontSize: "11px", padding: "2px 8px", borderRadius: "10px", fontWeight: "normal" },

  // Skip note
  skipNote: { background: "rgba(255,152,0,0.08)", border: "1px solid rgba(255,152,0,0.2)", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#ff9800", marginBottom: "16px", lineHeight: 1.6 },

  noShopCard: { background: "var(--bg-card)", borderRadius: "20px", padding: "40px", textAlign: "center", maxWidth: "400px", border: "1px solid var(--border)" },
  noShopTitle: { fontSize: "1.4rem", color: "white", marginBottom: "10px" },
  noShopText: { color: "var(--text-muted)", marginBottom: "24px" },
  createShopBtn: { background: "var(--accent)", color: "white", padding: "13px 28px", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px", fontWeight: "bold" },
  successCard: { background: "var(--bg-card)", borderRadius: "20px", padding: "40px 32px", maxWidth: "420px", width: "100%", textAlign: "center", border: "1px solid var(--border)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" },
  successTitle: { fontSize: "1.6rem", color: "white", marginBottom: "8px" },
  successText: { color: "var(--text-muted)", marginBottom: "24px" },
  nextSteps: { background: "var(--bg-secondary)", borderRadius: "12px", padding: "16px", marginBottom: "24px", textAlign: "left", border: "1px solid var(--border)" },
  nextStepsTitle: { fontWeight: "bold", marginBottom: "10px", color: "white", fontSize: "14px" },
  nextStep: { color: "#4caf50", fontSize: "13px", marginBottom: "6px" },
  nextStepPending: { color: "#ff9800", fontSize: "13px" },
  successBtns: { display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" },
  dashBtn: { background: "var(--accent)", color: "white", padding: "12px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "bold" },
  viewBtn: { background: "var(--bg-secondary)", color: "var(--text-secondary)", padding: "12px 20px", border: "1px solid var(--border)", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
};